<template>
    <div class="editor-wrapper">
        <div
            ref="editor"
            class="editor"
            contenteditable="true"
            @input="onInput"
            @paste="handlePaste"
            @focus="isFocused = true"
            @blur="isFocused = false"
        ></div>
        <div v-if="showPlaceholder" class="placeholder" @click="focusEditor">
            {{ placeholder }}
        </div>
        <div ref="mirror" class="mirror"></div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onUpdated, nextTick } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: '请输入内容...',
    },
});
const emit = defineEmits(['update:modelValue']);

const editor = ref(null);
const mirror = ref(null);
const isFocused = ref(false);

function insertTextAtCursor(text) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    range.deleteContents(); // 删除选中文本（如有）
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // 将光标移到新插入文本的末尾
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    sel.removeAllRanges();
    sel.addRange(range);
}

const syncHeight = () => {
    mirror.value.innerHTML = editor.value.innerHTML;
    editor.value.style.height = `${mirror.value.offsetHeight}px`;
};

const onInput = () => {
    syncHeight();
    emit('update:modelValue', editor.value.innerText);
};

const handlePaste = e => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    insertTextAtCursor(text);
    syncHeight();
};

const showPlaceholder = computed(
    () => !isFocused.value && (!editor.value?.innerText || editor.value.innerText.trim() === ''),
);

const focusEditor = () => {
    editor.value.focus();
};

onMounted(async () => {
    console.log('Editor mounted', props.modelValue);
    if (props.modelValue) {
        editor.value.innerText = props.modelValue;
    }
    await nextTick();
    syncHeight();
});

watch(
    () => props.modelValue,
    val => {
        if (val !== editor.value.innerText) {
            editor.value.innerText = val || '';
            syncHeight();
        }
    },
);
</script>

<style scoped>
.editor-wrapper {
    width: 100%;
    height: auto;
    position: relative;
    max-width: 80vw;
}

.editor {
    min-height: 40px;
    max-height: 40vh;
    padding: 10px;
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.5;
    overflow-y: hidden;
    outline: none;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-y: scroll;
}

.placeholder {
    position: absolute;
    top: 10px;
    left: 10px;
    color: #aaa;
    pointer-events: none;
    font-size: 16px;
    line-height: 1.5;
    user-select: none;
}

.mirror {
    visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 10px;
    font-size: 16px;
    line-height: 1.5;
    box-sizing: border-box;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
